import { SellPanel } from './SellPanel'
import { CrystalShop } from './CrystalShop'
import '../professions/professions.css'
import { LocationWindow } from '../LocationWindow'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { CatalogItem, Loadout, Shop, ShopOffer, ShopRecipe } from '@mezha/api'
import { useBuyMutation, useCraftMutation, useShopQuery } from '../../api/endpoints/trade'
import { useLoadoutQuery } from '../../api/endpoints/hero'
import { fromFailure, type ApiFailure } from '../../api/baseApi'
import { useCommand } from '../../lib/useCommand'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { ConfirmInPlace, PlankButton, PriceButton, Sheet, cx, rarityClass } from '../../ui'
import { CATEGORY_RU, CompareBlock, ItemArt, KIND_RU, VERDICT_RU, compareWithWorn, gearFacts, modList, rarityRu, silverText, restoreList, unmetOf } from '../common'
import './shop.css'

/** Most of one good bought by one command (the server's limit). */
const MAX_QTY = 20

type Pick = { kind: 'offer'; id: string } | { kind: 'recipe'; id: string }

/** `/play/shop/:id`: a trader in the hero's place — goods for silver and the master's recipes. */
export function ShopScreen() {
  useShellMode('window')
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const phone = usePhone()
  const shop = useShopQuery(id)
  const s = shop.data
  const [params] = useSearchParams()
  const requestedRecipe = params.get('recipe')
  const [tab, setTab] = useState<'goods' | 'craft' | 'sell'>(params.get('tab') === 'sell' ? 'sell' : params.get('tab') === 'craft' ? 'craft' : 'goods')
  const [picked, setPicked] = useState<Pick | null>(null)
  const [qty, setQty] = useState(1)
  const [done, setDone] = useState<string | null>(null)
  const [buy] = useBuyMutation()
  const [craft] = useCraftMutation()
  const cmd = useCommand(() => void shop.refetch())
  const dispatch = useAppDispatch()
  const loadout = useLoadoutQuery().data
  const appliedRecipeLink = useRef('')
  // On a computer the card is never empty: the first good (or recipe) is open.
  useEffect(() => {
    if (!s) return
    const link = `${s.id}:${requestedRecipe ?? ''}`
    if (appliedRecipeLink.current !== link) {
      appliedRecipeLink.current = link
      if (requestedRecipe && s.recipes.some(r => r.id === requestedRecipe)) {
        setTab('craft')
        setPicked({ kind: 'recipe', id: requestedRecipe })
        return
      }
    }
    if (phone || picked) return
    if (tab === 'goods' && s.offers[0]) setPicked({ kind: 'offer', id: s.offers[0].item.id })
    else if (tab === 'craft' && s.recipes[0]) setPicked({ kind: 'recipe', id: s.recipes[0].id })
  }, [phone, s, picked, tab, requestedRecipe])

  const offer = picked?.kind === 'offer' ? s?.offers.find(o => o.item.id === picked.id) ?? null : null
  const recipe = picked?.kind === 'recipe' ? s?.recipes.find(r => r.id === picked.id) ?? null : null
  useEffect(() => { setQty(1); setDone(null); cmd.clear() }, [picked?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const affordable = offer && s ? Math.max(1, Math.min(MAX_QTY, offer.item.stack_limit > 1 ? Math.floor(Number(s.silver) / offer.price) : 1)) : 1
  const count = Math.min(qty, affordable)
  // Buying and crafting always ask «…за N серебра?» in place of the pressed button (owner's decision, v04).
  const onBuy = async (o: ShopOffer, n: number) => {
    if (!s) return
    const r = await cmd.run(() => buy({ shop_id: s.id, expected_version: s.version, item_id: o.item.id, quantity: n }).unwrap())
    if (r) {
      const text = `Куплено: ${o.item.name}${n > 1 ? ` ×${n}` : ''}`
      setDone(text)
      dispatch(ui.actions.notice(`${text} · −${silverText(o.price * n)}`))
    }
  }
  const onCraft = async (rc: ShopRecipe) => {
    if (!s) return
    const r = await cmd.run(() => craft({ shop_id: s.id, expected_version: s.version, recipe_id: rc.id }).unwrap())
    if (r) {
      const text = `Готово: ${rc.output.name}${rc.amount > 1 ? ` ×${rc.amount}` : ''}`
      setDone(text)
      dispatch(ui.actions.notice(`${text} — в суме`))
    }
  }

  const failure = shop.isError && !s ? fromFailure(shop.error as ApiFailure) : null
  if (s?.crystal_shop) return <CrystalShop shop={s} refresh={shop.refetch} />
  if (failure && (failure.code === 'shop_not_here' || failure.code === 'shop_not_found')) {
    return (
      <LocationWindow title="Лавка" emblem="loc.shop" idBase="shop" bodyClassName="shop">
        <Empty>
          <b>{failure.code === 'shop_not_here' ? 'Торговец в другом месте.' : 'Такой лавки нет.'}</b>
          Лавки открываются в местах, где стоит торговец: зайдите к нему и выберите его метку на сцене.
          <div className="actions"><PlankButton onClick={() => navigate('/play')}>К локации</PlankButton></div>
        </Empty>
      </LocationWindow>
    )
  }

  const tabs = [{ id: 'goods', label: 'Товары' }, { id: 'sell', label: 'Продать' }, ...(s?.recipes.length ? [{ id: 'craft', label: 'Ремесло' }] : [])]
  const card = s && (offer
    ? <OfferCard key={offer.item.id} shop={s} offer={offer} loadout={loadout} qty={count} max={affordable} onQty={setQty} onBuy={() => onBuy(offer, count)} />
    : recipe ? <RecipeCard key={recipe.id} shop={s} recipe={recipe} loadout={loadout} onCraft={() => onCraft(recipe)} /> : null)
  const note = (done || cmd.text) && (
    <p className={cx('shop__note', cmd.text && 'is-error')} role={cmd.text ? 'alert' : 'status'}>{cmd.text ?? done}</p>
  )

  return (
    <LocationWindow title={s?.name ?? 'Лавка'} emblem="loc.shop" idBase="shop" bodyClassName="shop"
      tabs={tabs} activeTab={tabs ? tab : undefined} onTab={t => { setTab(t as 'goods' | 'craft' | 'sell'); setPicked(null) }}>
      {shop.isLoading && <Loading />}
      {shop.isError && !s && <Failure error={shop.error} retry={() => void shop.refetch()} what="Лавка" />}
      {s && tab === 'sell' && <SellPanel shop={s} refetch={() => void shop.refetch()} />}
      {s && tab !== 'sell' && (
        <>
          <div className="shop__side">
            <Link className="profession-entry" to="/play/professions">Мирные мастерства и новые рецепты →</Link>
            <div className="shop__trader">
              <span className="shop__face">{s.character?.portrait ? <img src={s.character.portrait} alt="" /> : <Emblem name="nav.hero" size={48} />}</span>
              <div className="shop__speech parch">
                <b>{s.character?.name ?? s.name}</b>
                {s.greeting && <p>«{s.greeting}»</p>}
              </div>
            </div>
            <div className="shop__purse"><Emblem name="res.silver" size={18} />У вас {silverText(Number(s.silver))}</div>
            {tab === 'goods' || !tabs
              ? <GoodsList shop={s} loadout={loadout} picked={picked} onPick={setPicked} onBuy={o => onBuy(o, 1)} />
              : <RecipeList shop={s} loadout={loadout} picked={picked} onPick={setPicked} onCraft={onCraft} />}
          </div>
          {!phone && (
            <div className="shop__card">
              {card ?? <p className="note">{tab === 'craft' && tabs ? 'Выберите, что сделать: мастер скажет, что нужно принести. «Сделать» у строки спросит подтверждение там же.' : 'Нажмите цену — лавка спросит «Купить?» прямо в строке. Нажмите строку — карточка с ценой и свойствами.'}</p>}
              {note}
            </div>
          )}
          {phone && (
            <Sheet open={Boolean(card)} title={offer?.item.name ?? recipe?.output.name ?? ''} emblem="loc.shop" onClose={() => setPicked(null)}>
              {card}
              {note}
            </Sheet>
          )}
        </>
      )}
    </LocationWindow>
  )
}

/** The line under a good's name: what it is and its main numbers. */
function summary(it: CatalogItem): string {
  const what = it.kind === 'gear' ? CATEGORY_RU[it.slot_category] ?? it.slot_category : (KIND_RU[it.kind] ?? it.kind).toLowerCase()
  const nums = [...modList(it.modifiers).slice(0, 2).map(m => `${m.label.toLowerCase()} ${m.text}`), ...restoreList(it.use).slice(0, 1).map(u => `${u.label.toLowerCase()} ${u.text}`)]
  return [what, ...nums].join(' · ')
}

function GoodsList({ shop, loadout, picked, onPick, onBuy }: { shop: Shop; loadout: Loadout | undefined; picked: Pick | null; onPick: (p: Pick) => void; onBuy: (o: ShopOffer) => Promise<unknown> }) {
  if (shop.offers.length === 0) return <Empty>Сегодня товара нет.</Empty>
  return (
    <ul className="shop__list" aria-label="Товары">
      {shop.offers.map(o => {
        const on = picked?.kind === 'offer' && picked.id === o.item.id
        const short = o.price > Number(shop.silver)
        return (
          <li key={o.item.id} className="srow">
            <ConfirmInPlace kind="row" question={<>Купить «{o.item.name}» за <b>{silverText(o.price)}</b>?</>} onConfirm={() => onBuy(o)}>
              {ask => (
                <>
                  <button type="button" aria-pressed={on} className={cx('mode', on && 'is-selected', short && 'is-locked')}
                    onClick={() => onPick({ kind: 'offer', id: o.item.id })}>
                    <span className={cx('shop__art', rarityClass(o.item.rarity))}><ItemArt item={o.item} size={44} /></span>
                    <span className="shop__row">
                      <b className={rarityClass(o.item.rarity)}>{o.item.name}</b>
                      <small>{summary(o.item)}{o.have > 0 ? ` · у вас ${o.have}` : ''}</small>
                      <Verdict item={o.item} loadout={loadout} />
                    </span>
                  </button>
                  <PriceButton locked={short} onPress={ask} aria-label={short ? `Не хватает серебра на «${o.item.name}»: ${silverText(o.price)}` : `Купить «${o.item.name}» за ${silverText(o.price)}`}>
                    {short ? <>не хватает<small>{o.price}</small></> : <><Emblem name="res.silver" />{o.price}</>}
                  </PriceButton>
                </>
              )}
            </ConfirmInPlace>
          </li>
        )
      })}
    </ul>
  )
}

function RecipeList({ shop, loadout, picked, onPick, onCraft }: { shop: Shop; loadout: Loadout | undefined; picked: Pick | null; onPick: (p: Pick) => void; onCraft: (r: ShopRecipe) => Promise<unknown> }) {
  return (
    <ul className="shop__list" aria-label="Рецепты">
      {shop.recipes.map(r => {
        const on = picked?.kind === 'recipe' && picked.id === r.id
        const why = r.ready ? 'можно сделать' : r.block ? r.block : r.level_required > shop.level ? `с ${r.level_required} уровня` : 'не хватает'
        return (
          <li key={r.id} className="srow">
            <ConfirmInPlace kind="row" question={<>Сделать «{r.output.name}»{r.amount > 1 ? ` ×${r.amount}` : ''} за <b>{silverText(r.silver)}</b>?</>} onConfirm={() => onCraft(r)}>
              {ask => (
                <>
                  <button type="button" aria-pressed={on} className={cx('mode', on && 'is-selected', !r.ready && 'is-locked')}
                    onClick={() => onPick({ kind: 'recipe', id: r.id })}>
                    <span className={cx('shop__art', rarityClass(r.output.rarity))}><ItemArt item={r.output} size={44} /></span>
                    <span className="shop__row"><b className={rarityClass(r.output.rarity)}>{r.output.name}{r.amount > 1 ? ` ×${r.amount}` : ''}</b><small>{silverText(r.silver)} · {why}</small><Verdict item={r.output} loadout={loadout} /></span>
                  </button>
                  <PriceButton locked={!r.ready} onPress={ask} aria-label={r.ready ? `Сделать «${r.output.name}» за ${silverText(r.silver)}` : `«${r.output.name}»: ${why}`}>
                    {r.ready ? <><Emblem name="loc.shop" />Сделать</> : why}
                  </PriceButton>
                </>
              )}
            </ConfirmInPlace>
          </li>
        )
      })}
    </ul>
  )
}

/** A small mark in a goods row: better or worse than what is worn, or unwearable yet. */
function Verdict({ item, loadout }: { item: CatalogItem; loadout: Loadout | undefined }) {
  const cmp = compareWithWorn(item, loadout)
  if (!cmp) return null
  const unmet = unmetOf(item, loadout?.hero.attributes)
  if (unmet || item.level_required > (loadout?.hero.level ?? 0)) return <span className="verdict verdict--locked">пока не надеть</span>
  if (cmp.verdict === 'same') return null
  return <span className={`verdict verdict--${cmp.verdict}`}>{VERDICT_RU[cmp.verdict]}</span>
}

function ItemHead({ item }: { item: CatalogItem }) {
  const kindLine = item.kind === 'gear'
    ? `${rarityRu(item.rarity)} · ${CATEGORY_RU[item.slot_category] ?? item.slot_category}`
    : `${rarityRu(item.rarity)} · ${(KIND_RU[item.kind] ?? item.kind).toLowerCase()}`
  return (
    <>
      <div className="itemcard__head shop__head">
        <span className={cx('shop__bigart', rarityClass(item.rarity))}><ItemArt item={item} size={88} /></span>
        <div><b>{item.name}</b><span className="rarity-line">{kindLine}{item.level_required > 1 ? ` · ур. ${item.level_required}` : ''}</span></div>
      </div>
      {item.description && <p className="itemcard__text">{item.description}</p>}
    </>
  )
}

function OfferCard({ shop, offer, loadout, qty, max, onQty, onBuy }: { shop: Shop; offer: ShopOffer; loadout: Loadout | undefined; qty: number; max: number; onQty: (n: number) => void; onBuy: () => Promise<unknown> }) {
  const it = offer.item
  const cmp = compareWithWorn(it, loadout)
  const total = offer.price * qty
  const short = total > Number(shop.silver)
  return (
    <div className={cx('itemcard parch', rarityClass(it.rarity))}>
      <ItemHead item={it} />
      <div className="mods">
        {modList(it.modifiers).map(m => <Pair key={m.key} a={m.label} b={m.text} />)}
        {gearFacts(it, loadout?.hero.attributes).map(f => <Pair key={f.key} a={f.label} b={f.text} down={f.down} />)}
        {restoreList(it.use).map(u => <Pair key={u.key} a={u.label} b={u.text} />)}
        <Pair a="Цена" b={silverText(offer.price)} ink />
        {offer.have > 0 && <Pair a="У вас уже" b={String(offer.have)} ink />}
        {it.level_required > shop.level && <Pair a="Надеть можно" b={`с ${it.level_required} уровня`} down />}
      </div>
      {cmp && <CompareBlock cmp={cmp} />}
      {it.stack_limit > 1 && (
        <div className="shop__qty" role="group" aria-label="Сколько купить">
          <button type="button" aria-label="Меньше" disabled={qty <= 1} onClick={() => onQty(qty - 1)}>−</button>
          <output aria-live="polite">{qty}</output>
          <button type="button" aria-label="Больше" disabled={qty >= max} onClick={() => onQty(qty + 1)}>+</button>
          <span>{silverText(total)}</span>
        </div>
      )}
      <ConfirmInPlace question={<>Купить «{it.name}»{qty > 1 ? ` ×${qty}` : ''} за <b>{silverText(total)}</b>?</>} yes="Да, купить" onConfirm={onBuy}>
        {ask => (
          <div className="actions">
            <PlankButton gold locked={short} onClick={ask}>{short ? `Не хватает серебра` : `Купить${qty > 1 ? ` ×${qty}` : ''} за ${silverText(total)}`}</PlankButton>
          </div>
        )}
      </ConfirmInPlace>
    </div>
  )
}

function RecipeCard({ shop, recipe, loadout, onCraft }: { shop: Shop; recipe: ShopRecipe; loadout: Loadout | undefined; onCraft: () => Promise<unknown> }) {
  const it = recipe.output
  const cmp = compareWithWorn(it, loadout)
  const lowLevel = recipe.level_required > shop.level
  return (
    <div className={cx('itemcard parch', rarityClass(it.rarity))}>
      <ItemHead item={it} />
      <div className="mods">
        {modList(it.modifiers).map(m => <Pair key={m.key} a={m.label} b={m.text} />)}
        {gearFacts(it, loadout?.hero.attributes).map(f => <Pair key={f.key} a={f.label} b={f.text} down={f.down} />)}
        {restoreList(it.use).map(u => <Pair key={u.key} a={u.label} b={u.text} />)}
      </div>
      {cmp && <CompareBlock cmp={cmp} />}
      <div className="mods" aria-label="Что нужно">
        {recipe.inputs.map(i => <Pair key={i.item.id} a={i.item.name} b={`${Math.min(i.have, i.amount)} из ${i.amount}`} down={i.have < i.amount} />)}
        <Pair a="Плата мастеру" b={silverText(recipe.silver)} down={recipe.silver > Number(shop.silver)} />
        {recipe.level_required > 1 && <Pair a="Уровень" b={String(recipe.level_required)} down={lowLevel} />}
        {recipe.profession_id && <Pair a="Мастерство" b={recipe.block || `Уровень ${recipe.profession_level} — доступно`} down={Boolean(recipe.block)} />}
      </div>
      {recipe.inputs.some(i => i.item.kind === 'gear') && <p className="note shop__hint">Надетые вещи мастер не берёт: снимите их, если хотите переделать.</p>}
      <ConfirmInPlace question={<>Сделать «{it.name}»{recipe.amount > 1 ? ` ×${recipe.amount}` : ''} за <b>{silverText(recipe.silver)}</b>?</>} yes="Да, сделать" onConfirm={onCraft}>
        {ask => (
          <div className="actions">
            <PlankButton gold locked={!recipe.ready} onClick={ask}>{recipe.ready ? `Сделать за ${silverText(recipe.silver)}` : recipe.block || (lowLevel ? `С ${recipe.level_required} уровня` : 'Не хватает')}</PlankButton>
          </div>
        )}
      </ConfirmInPlace>
    </div>
  )
}

function Pair({ a, b, down, ink }: { a: string; b: string; down?: boolean; ink?: boolean }) {
  return <><span>{a}</span><u className={cx(down && 'down', ink && 'ink')}>{b}</u></>
}
